import React, { createContext, useContext, useState, useCallback } from 'react';
import { vessels as mockVessels, geofences as mockGeofences, stations as mockStations } from '../data/mockData';

const AppContext = createContext(null);

export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
};

export const AppProvider = ({ children }) => {
  // Authentication state (mocked)
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Map state
  const [mapCenter, setMapCenter] = useState([-5.12, 119.42]);
  const [mapZoom, setMapZoom] = useState(13);
  const [mapStyle, setMapStyle] = useState('osm'); // 'osm' or 'satellite'

  // Vessel state
  const [vessels, setVessels] = useState(mockVessels);
  const [selectedVessel, setSelectedVessel] = useState(null);
  const [vesselDrawerOpen, setVesselDrawerOpen] = useState(false);

  // Geofence state
  const [geofences, setGeofences] = useState(mockGeofences);
  const [selectedGeofence, setSelectedGeofence] = useState(null);
  const [geofenceModalOpen, setGeofenceModalOpen] = useState(false);
  const [isDrawingGeofence, setIsDrawingGeofence] = useState(false);

  // Station state
  const [stations, setStations] = useState(mockStations);
  const [selectedStation, setSelectedStation] = useState(null);

  // Search state
  const [searchQuery, setSearchQuery] = useState('');

  // Sidebar state
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  // Login function
  const login = useCallback((email, password) => {
    // Mocked authentication
    if (email && password) {
      setUser({
        id: '1',
        name: 'Admin Pelindo',
        email: email,
        role: 'admin'
      });
      setIsAuthenticated(true);
      return true;
    }
    return false;
  }, []);

  // Logout function
  const logout = useCallback(() => {
    setUser(null);
    setIsAuthenticated(false);
  }, []);

  // Vessel selection
  const selectVessel = useCallback((vessel) => {
    setSelectedVessel(vessel);
    setVesselDrawerOpen(true);
    if (vessel) {
      setMapCenter([vessel.lat, vessel.lon]);
    }
  }, []);

  const closeVesselDrawer = useCallback(() => {
    setVesselDrawerOpen(false);
    setTimeout(() => setSelectedVessel(null), 300);
  }, []);

  // Geofence selection
  const selectGeofence = useCallback((geofence) => {
    setSelectedGeofence(geofence);
    setGeofenceModalOpen(true);
    if (geofence && geofence.polygonCoords.length > 0) {
      const centerLat = geofence.polygonCoords.reduce((sum, c) => sum + c[0], 0) / geofence.polygonCoords.length;
      const centerLon = geofence.polygonCoords.reduce((sum, c) => sum + c[1], 0) / geofence.polygonCoords.length;
      setMapCenter([centerLat, centerLon]);
    }
  }, []);

  const closeGeofenceModal = useCallback(() => {
    setGeofenceModalOpen(false);
    setTimeout(() => setSelectedGeofence(null), 300);
  }, []);

  // Station selection
  const selectStation = useCallback((station) => {
    setSelectedStation(station);
    if (station && station.boundaryCoords.length > 0) {
      const centerLat = station.boundaryCoords.reduce((sum, c) => sum + c[0], 0) / station.boundaryCoords.length;
      const centerLon = station.boundaryCoords.reduce((sum, c) => sum + c[1], 0) / station.boundaryCoords.length;
      setMapCenter([centerLat, centerLon]);
      setMapZoom(15);
    }
  }, []);

  // Search vessels
  const searchVessels = useCallback((query) => {
    setSearchQuery(query);
    if (!query) return mockVessels;
    const lowerQuery = query.toLowerCase();
    return mockVessels.filter(v => 
      v.name.toLowerCase().includes(lowerQuery) ||
      v.mmsi.includes(query) ||
      v.imo.includes(query) ||
      v.callsign.toLowerCase().includes(lowerQuery)
    );
  }, []);

  // Toggle geofence auto gate
  const toggleGeofenceAutoGate = useCallback((geofenceId) => {
    setGeofences(prev => prev.map(g => 
      g.id === geofenceId ? { ...g, autoGate: !g.autoGate } : g
    ));
  }, []);

  // Create new geofence (mocked)
  const createGeofence = useCallback((geofenceData) => {
    const newGeofence = {
      ...geofenceData,
      id: `gf${Date.now()}`,
      createdAt: new Date().toISOString(),
      autoGate: false
    };
    setGeofences(prev => [...prev, newGeofence]);
    setIsDrawingGeofence(false);
    return newGeofence;
  }, []);

  // Delete geofence
  const deleteGeofence = useCallback((geofenceId) => {
    setGeofences(prev => prev.filter(g => g.id !== geofenceId));
    if (selectedGeofence?.id === geofenceId) {
      closeGeofenceModal();
    }
  }, [selectedGeofence, closeGeofenceModal]);

  const value = {
    // Auth
    user,
    isAuthenticated,
    login,
    logout,

    // Map
    mapCenter,
    setMapCenter,
    mapZoom,
    setMapZoom,
    mapStyle,
    setMapStyle,

    // Vessels
    vessels,
    setVessels,
    selectedVessel,
    setSelectedVessel,
    vesselDrawerOpen,
    setVesselDrawerOpen,
    selectVessel,
    closeVesselDrawer,

    // Geofences
    geofences,
    setGeofences,
    selectedGeofence,
    setSelectedGeofence,
    geofenceModalOpen,
    setGeofenceModalOpen,
    selectGeofence,
    closeGeofenceModal,
    isDrawingGeofence,
    setIsDrawingGeofence,
    toggleGeofenceAutoGate,
    createGeofence,
    deleteGeofence,

    // Stations
    stations,
    setStations,
    selectedStation,
    setSelectedStation,
    selectStation,

    // Search
    searchQuery,
    setSearchQuery,
    searchVessels,

    // Sidebar
    sidebarCollapsed,
    setSidebarCollapsed
  };

  return (
    <AppContext.Provider value={value}>
      {children}
    </AppContext.Provider>
  );
};

export default AppContext;
